import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  limit,
  Timestamp,
  onSnapshot,
  Unsubscribe,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { httpsCallable, functions } from '@/lib/functions';
import { trackSanitizationIntercept } from '@/services/sanitizationTelemetry';

export type TravelAdvisory = {
  id: string;
  country: string;
  countryCode: string;
  /** City or region label for display when advisory is city-specific (e.g. "New York City") */
  locationLabel?: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  type:
    | 'government'
    | 'security'
    | 'health'
    | 'natural_disaster'
    | 'political'
    | 'entry_requirements'
    | 'transport'
    | 'news';
  title: string;
  description: string;
  issuedAt: Date;
  expiresAt?: Date;
  source: 'step' | 'manual' | 'government' | 'travelbuddy' | 'aerodatabox' | 'gnews' | 'google_news_rss';
  url?: string;
  isRead?: boolean;
};

export type FirestoreTravelAdvisory = {
  country: string;
  countryCode: string;
  locationLabel?: string;
  severity: string;
  type: string;
  title: string;
  description: string;
  issuedAt: Timestamp | string;
  expiresAt?: Timestamp | string | null;
  source: string;
  url?: string;
  isRead?: boolean;
};

export type AdvisorySubscriptionStatus = {
  source: 'travel';
  state: 'ready' | 'error';
  message?: string;
  code?: string;
};

export type AdvisoryFetchStatus = 'updated' | 'noCoverage' | 'sourceUnavailable' | 'rateLimited';
export type DestinationCityMap = Map<string, Set<string>>;

const severityOrder: Record<TravelAdvisory['severity'], number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
};
const travelAdvisoryTypes: ReadonlySet<TravelAdvisory['type']> = new Set([
  'government',
  'security',
  'health',
  'natural_disaster',
  'political',
  'entry_requirements',
  'transport',
  'news',
]);
const severityRank: Record<TravelAdvisory['severity'], number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
};
const countryFallbackTypes: ReadonlySet<TravelAdvisory['type']> = new Set([
  'entry_requirements',
  'transport',
  'natural_disaster',
  'health',
]);
const travelNewsSignalRegex =
  /\b(travel|traveler|tourist|tourism|airport|flight|airline|rail|train|metro|subway|ferry|bus|transit|road closure|traffic disruption|visa|entry requirement|border control|weather alert|storm|flood|wildfire|evacuation|curfew|security alert)\b/i;

/** Regional terms that still matter for a metro destination (e.g. NYC ↔ airports, boroughs, commuter rail). */
const DESTINATION_REGIONAL_SYNONYMS: Record<string, readonly string[]> = {
  'new york city': [
    'Westchester',
    'Long Island',
    'LIRR',
    'Metro-North',
    'Metro North',
    'NJ Transit',
    'Newark',
    'JFK',
    'LaGuardia',
    'La Guardia',
    'Hudson Valley',
    'Bronx',
    'Brooklyn',
    'Queens',
    'Manhattan',
    'Staten Island',
    'Freeport',
    'Nassau',
    'Suffolk',
  ],
  'new york': [
    'Westchester',
    'Long Island',
    'LIRR',
    'Metro-North',
    'NJ Transit',
    'Newark',
    'JFK',
    'LaGuardia',
    'Bronx',
    'Brooklyn',
    'Queens',
    'Manhattan',
    'Staten Island',
  ],
};

/** Keywords indicating travel impact (shutdowns, delays, federal ops affecting trips). */
const TRAVEL_IMPACT_SIGNAL_REGEX =
  /\b(flight|airport|airline|passenger|travelers?|shutdown|delay|cancellation|cancel|disruption|strike|ground stop|tsa|dhs)\b/i;

export const textHasTravelImpactSignal = (text: string | undefined): boolean => {
  if (!text?.trim()) return false;
  return TRAVEL_IMPACT_SIGNAL_REGEX.test(text);
};

export const getRegionalSynonymsForDestinations = (destinationCities: ReadonlySet<string>): string[] => {
  const out = new Set<string>();
  for (const city of destinationCities) {
    const n = normalizePlaceText(city);
    if (!n) continue;
    for (const [key, syns] of Object.entries(DESTINATION_REGIONAL_SYNONYMS)) {
      if (n.includes(key) || key.includes(n)) {
        for (const s of syns) {
          const sn = normalizePlaceText(s);
          if (sn) out.add(sn);
        }
      }
    }
  }
  return Array.from(out);
};

export const textMatchesRegionalSynonyms = (text: string, synonyms: readonly string[]): boolean => {
  if (!synonyms.length || !text.trim()) return false;
  const norm = normalizePlaceText(text);
  return synonyms.some((s) => s && norm.includes(s));
};

export const normalizePlaceText = (value?: string | null): string => {
  const source = (value || '').trim().toLowerCase();
  if (!source) return '';
  return source
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

export const locationMatchesDestination = (
  locationLabel: string | undefined,
  destinationCities: ReadonlySet<string>
): boolean => {
  if (destinationCities.size === 0) return true;
  const normalizedLocation = normalizePlaceText(locationLabel);
  if (!normalizedLocation) return false;

  for (const city of destinationCities) {
    if (!city) continue;
    if (normalizedLocation.includes(city) || city.includes(normalizedLocation)) {
      return true;
    }
  }
  return false;
};

const advisoryCityMatch = (advisory: TravelAdvisory, destinationCities: ReadonlySet<string>): boolean => {
  if (destinationCities.size === 0) return false;
  return (
    locationMatchesDestination(advisory.locationLabel, destinationCities) ||
    locationMatchesDestination(advisory.title, destinationCities) ||
    locationMatchesDestination(advisory.description, destinationCities)
  );
};

const isHighValueCountryFallback = (advisory: TravelAdvisory): boolean => {
  if (countryFallbackTypes.has(advisory.type)) return true;
  if (advisory.type === 'security' || advisory.type === 'government') {
    return advisory.severity === 'high' || advisory.severity === 'critical';
  }
  if (advisory.type === 'news') {
    const text = `${advisory.title} ${advisory.description}`;
    const travel = travelNewsSignalRegex.test(text);
    const impact = textHasTravelImpactSignal(text);
    if (advisory.severity === 'low') {
      return impact;
    }
    return travel || impact;
  }
  return false;
};

export const filterAdvisoriesForDestinations = (
  advisories: TravelAdvisory[],
  destinationCitiesByCountry: DestinationCityMap
): TravelAdvisory[] => {
  return advisories.filter((advisory) => {
    const countryCode = advisory.countryCode.toUpperCase();
    const destinationCities = destinationCitiesByCountry.get(countryCode) ?? new Set<string>();
    if (destinationCities.size === 0) {
      return true;
    }

    if (advisoryCityMatch(advisory, destinationCities)) {
      return true;
    }

    return isHighValueCountryFallback(advisory);
  });
};

export const rankTravelAdvisories = (
  advisories: TravelAdvisory[],
  destinationCitiesByCountry: DestinationCityMap
): TravelAdvisory[] => {
  return [...advisories].sort((a, b) => {
    const aCities = destinationCitiesByCountry.get(a.countryCode.toUpperCase()) ?? new Set<string>();
    const bCities = destinationCitiesByCountry.get(b.countryCode.toUpperCase()) ?? new Set<string>();
    const aCityMatch = advisoryCityMatch(a, aCities) ? 1 : 0;
    const bCityMatch = advisoryCityMatch(b, bCities) ? 1 : 0;
    if (aCityMatch !== bCityMatch) {
      return bCityMatch - aCityMatch;
    }

    // All advisories are destination-country scoped by subscription; keep this signal explicit for future mixed lists.
    const aCountryMatch = destinationCitiesByCountry.has(a.countryCode.toUpperCase()) ? 1 : 0;
    const bCountryMatch = destinationCitiesByCountry.has(b.countryCode.toUpperCase()) ? 1 : 0;
    if (aCountryMatch !== bCountryMatch) {
      return bCountryMatch - aCountryMatch;
    }

    const severityDelta = severityRank[a.severity] - severityRank[b.severity];
    if (severityDelta !== 0) {
      return severityDelta;
    }
    return b.issuedAt.getTime() - a.issuedAt.getTime();
  });
};

const parseTravelAdvisoryDoc = (docSnap: { id: string; data: () => unknown }): TravelAdvisory | null => {
  const data = docSnap.data() as FirestoreTravelAdvisory;
  const expiresAt = data.expiresAt
    ? data.expiresAt instanceof Timestamp
      ? data.expiresAt.toDate()
      : new Date(data.expiresAt)
    : undefined;

  if (expiresAt && expiresAt < new Date()) {
    return null;
  }

  return {
    id: docSnap.id,
    country: data.country,
    countryCode: data.countryCode,
    locationLabel: data.locationLabel,
    severity: data.severity as TravelAdvisory['severity'],
    type: data.type as TravelAdvisory['type'],
    title: data.title,
    description: data.description,
    issuedAt: data.issuedAt instanceof Timestamp ? data.issuedAt.toDate() : new Date(data.issuedAt),
    expiresAt,
    source: data.source as TravelAdvisory['source'],
    url: data.url,
    isRead: data.isRead || false,
  };
};

const sortAndDedupe = (advisories: TravelAdvisory[]): TravelAdvisory[] => {
  const uniqueAdvisories = Array.from(new Map(advisories.map((adv) => [adv.id, adv])).values());
  uniqueAdvisories.sort((a, b) => {
    const severityDiff = severityOrder[a.severity] - severityOrder[b.severity];
    if (severityDiff !== 0) return severityDiff;
    return b.issuedAt.getTime() - a.issuedAt.getTime();
  });
  return uniqueAdvisories;
};

export const __testing = {
  sortAndDedupe,
  normalizePlaceText,
  locationMatchesDestination,
  filterAdvisoriesForDestinations,
  rankTravelAdvisories,
  textHasTravelImpactSignal,
  getRegionalSynonymsForDestinations,
  textMatchesRegionalSynonyms,
};

/**
 * Subscribe to travel advisories for a country
 */
export const subscribeToTravelAdvisories = (
  countryCode: string,
  callback: (advisories: TravelAdvisory[]) => void,
  onStatus?: (status: AdvisorySubscriptionStatus) => void
): Unsubscribe => {
  const advisoriesRef = collection(db, 'advisories');
  
  const q = query(
    advisoriesRef,
    where('countryCode', '==', countryCode),
    limit(120)
  );

  let unsubscribeFn: (() => void) | null = null;
  let isCleanedUp = false;

  try {
    unsubscribeFn = onSnapshot(
      q,
      (snapshot) => {
        // Don't process updates if already cleaned up
        if (isCleanedUp) {
          return;
        }

        try {
          const advisories = sortAndDedupe(
            snapshot.docs
              .map((docSnap) => parseTravelAdvisoryDoc(docSnap))
              .filter(
                (adv): adv is TravelAdvisory =>
                  adv !== null && travelAdvisoryTypes.has(adv.type)
              )
          );
          callback(advisories);
          onStatus?.({ source: 'travel', state: 'ready' });
        } catch (error) {
          console.error('[TravelAdvisories] Error processing snapshot:', error);
          onStatus?.({
            source: 'travel',
            state: 'error',
            message: error instanceof Error ? error.message : 'Failed to process travel advisories',
          });
        }
      },
      (error) => {
        // Don't process errors if already cleaned up
        if (isCleanedUp) {
          return;
        }

        console.error('[TravelAdvisories] Error subscribing to travel advisories:', error);
        onStatus?.({
          source: 'travel',
          state: 'error',
          code: error?.code,
          message: error?.message || 'Failed to subscribe to travel advisories',
        });
      }
    );
  } catch (error) {
    console.error('[TravelAdvisories] Error setting up subscription:', error);
    // Return a no-op cleanup function if setup fails
    return () => {};
  }

  return () => {
    if (isCleanedUp) {
      return; // Already cleaned up, prevent double cleanup
    }
    isCleanedUp = true;

    if (unsubscribeFn) {
      try {
        unsubscribeFn();
      } catch (error) {
        // Silently ignore cleanup errors - they're often due to Firestore internal state
      } finally {
        unsubscribeFn = null; // Clear reference after cleanup
      }
    }
  };
};

/**
 * Subscribe to travel advisories for multiple countries (e.g., user's trips)
 */
export const subscribeToMultipleCountryAdvisories = (
  countryCodes: string[],
  callback: (advisories: TravelAdvisory[]) => void,
  onStatus?: (status: AdvisorySubscriptionStatus) => void
): Unsubscribe => {
  if (countryCodes.length === 0) {
    callback([]);
    return () => {}; // Return no-op unsubscribe
  }

  const advisoriesRef = collection(db, 'advisories');
  
  // Note: Firestore 'in' queries are limited to 10 items
  // For more countries, we'd need to batch queries
  const batchSize = 10;
  const batches: string[][] = [];
  
  for (let i = 0; i < countryCodes.length; i += batchSize) {
    batches.push(countryCodes.slice(i, i + batchSize));
  }

  const unsubscribes: (() => void)[] = [];
  let isCleanedUp = false;
  const advisoriesByBatch = new Map<number, TravelAdvisory[]>();

  const emitMerged = () => {
    const merged = sortAndDedupe(Array.from(advisoriesByBatch.values()).flat());
    callback(merged);
    onStatus?.({ source: 'travel', state: 'ready' });
  };

  batches.forEach((batch, batchIndex) => {
    const q = query(
      advisoriesRef,
      where('countryCode', 'in', batch),
      limit(150)
    );

    try {
      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          // Don't process updates if already cleaned up
          if (isCleanedUp) {
            return;
          }

          try {
            const batchAdvisories = snapshot.docs
              .map((docSnap) => parseTravelAdvisoryDoc(docSnap))
              .filter(
                (adv): adv is TravelAdvisory =>
                  adv !== null && travelAdvisoryTypes.has(adv.type)
              );

            advisoriesByBatch.set(batchIndex, batchAdvisories);
            emitMerged();
          } catch (error) {
            console.error('[TravelAdvisories] Error processing snapshot:', error);
            onStatus?.({
              source: 'travel',
              state: 'error',
              message: error instanceof Error ? error.message : 'Failed to process advisory snapshot',
            });
          }
        },
        (error) => {
          // Don't process errors if already cleaned up
          if (isCleanedUp) {
            return;
          }

          console.error('[TravelAdvisories] Error subscribing to travel advisories:', error);
        onStatus?.({
          source: 'travel',
          state: 'error',
          code: error?.code,
          message: error?.message || 'Failed to subscribe to travel advisories',
        });
        }
      );

      unsubscribes.push(() => {
        try {
          unsubscribe();
        } catch (error) {
          // Silently ignore cleanup errors
        }
      });
    } catch (error) {
      console.error('[TravelAdvisories] Error setting up subscription for batch:', error);
    }
  });

  // Return function to unsubscribe from all
  return () => {
    if (isCleanedUp) {
      return; // Already cleaned up, prevent double cleanup
    }
    isCleanedUp = true;

    unsubscribes.forEach((unsub) => {
      try {
        unsub();
      } catch (error) {
        // Silently ignore cleanup errors - they're often due to Firestore internal state
      }
    });
  };
};

/**
 * Fetch travel advisories from STEP program (via cloud function)
 * This would typically be called by a cloud function that runs periodically
 */
export const fetchTravelAdvisories = async (
  countryCode: string
): Promise<{ success: boolean; status?: AdvisoryFetchStatus; countryCode?: string; error?: string }> => {
  try {
    const callFetchAdvisories = httpsCallable<
      { countryCode: string },
      { success: boolean; status?: AdvisoryFetchStatus; countryCode?: string; error?: string }
    >(
      functions,
      'fetchTravelAdvisories'
    );

    const result = await callFetchAdvisories({ countryCode });
    return {
      success: result.data.success,
      status: result.data.status,
      countryCode: result.data.countryCode,
      error: result.data.error,
    };
  } catch (error) {
    console.error('[TravelAdvisories] Error fetching travel advisories:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch travel advisories',
    };
  }
};

export const fetchEntryRequirementAdvisories = async (payload: {
  passportCountry: string;
  destinationCountry: string;
  tripStartDate?: string;
}): Promise<{ success: boolean; status?: AdvisoryFetchStatus; created?: number; error?: string }> => {
  try {
    const callable = httpsCallable<
      typeof payload,
      { success: boolean; status?: AdvisoryFetchStatus; created?: number; error?: string }
    >(functions, 'fetchEntryRequirementAdvisories');
    const result = await callable(payload);
    return result.data;
  } catch (error) {
    console.error('[TravelAdvisories] Error fetching entry requirement advisories:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch entry requirement advisories',
    };
  }
};

export const fetchTransportAdvisories = async (payload: {
  airlineCode: string;
  flightNumber: string;
  departureDate: string;
  tripId?: string;
  bookingId?: string;
}): Promise<{ success: boolean; status?: AdvisoryFetchStatus; created?: number; error?: string }> => {
  try {
    const callable = httpsCallable<
      typeof payload,
      { success: boolean; status?: AdvisoryFetchStatus; created?: number; error?: string }
    >(functions, 'fetchTransportAdvisories');
    const result = await callable(payload);
    return result.data;
  } catch (error) {
    console.error('[TravelAdvisories] Error fetching transport advisories:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch transport advisories',
    };
  }
};

export const fetchDestinationNews = async (payload: {
  countryCode?: string;
  country?: string;
  /** Optional city name for city-specific news (e.g. "New York City") */
  city?: string;
}): Promise<{ success: boolean; status?: AdvisoryFetchStatus; created?: number; countryCode?: string; error?: string }> => {
  try {
    const normalizeOptionalField = (value: string | undefined, minLength = 2): string | undefined => {
      if (typeof value !== 'string') return undefined;
      const trimmed = value.trim();
      return trimmed.length >= minLength ? trimmed : undefined;
    };

    const normalizedCountryCode = normalizeOptionalField(payload.countryCode)?.toUpperCase();
    const normalizedCountry = normalizeOptionalField(payload.country);
    const normalizedCity = normalizeOptionalField(payload.city);

    if (typeof payload.city === 'string' && payload.city.trim().length > 0 && !normalizedCity) {
      trackSanitizationIntercept({
        scope: 'travel_news',
        reason: 'empty_city',
        droppedFields: ['city'],
      });
    }

    if (!normalizedCountryCode && !normalizedCountry) {
      trackSanitizationIntercept({
        scope: 'travel_news',
        reason: 'missing_country',
        droppedFields: ['countryCode', 'country'],
      });
      return {
        success: false,
        error: 'countryCode or country is required',
      };
    }

    const requestPayload: typeof payload = {
      ...(normalizedCountryCode ? { countryCode: normalizedCountryCode } : {}),
      ...(normalizedCountry ? { country: normalizedCountry } : {}),
      ...(normalizedCity ? { city: normalizedCity } : {}),
    };

    const callable = httpsCallable<
      typeof payload,
      { success: boolean; status?: AdvisoryFetchStatus; created?: number; countryCode?: string; error?: string }
    >(functions, 'fetchDestinationNews');
    const result = await callable(requestPayload);
    return result.data;
  } catch (error) {
    console.error('[TravelAdvisories] Error fetching destination news:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch destination news',
    };
  }
};

/**
 * Mark advisory as read
 */
export const markAdvisoryAsRead = async (advisoryId: string): Promise<{ success: boolean; error?: string }> => {
  try {
    const advisoryRef = doc(db, 'advisories', advisoryId);
    await getDoc(advisoryRef).then(async (docSnap) => {
      if (docSnap.exists()) {
        // Note: In production, you'd update a user-specific read status
        // For now, this is a placeholder
      }
    });

    return { success: true };
  } catch (error) {
    console.error('[TravelAdvisories] Error marking advisory as read:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to mark advisory as read',
    };
  }
};

